import React, { createContext, useEffect, useRef, useState } from 'react';
import { cloneDeep, omit } from 'lodash';
import moment from 'moment';
import uuid from 'uuid';

import { useAuth } from 'contexts/auth';
import { useInstructor } from 'contexts/instructorRole';
import { useUser2 } from 'contexts/user2';
import { useSnackbar } from 'contexts/snackbar';
import courseService from 'services/course';
import Milestones from 'pages/TaskManager/Milestones';
import TaskTemplates from 'pages/TaskManager/TaskTemplates';
import { TYPES } from 'utils/constants';
import { getDuplicateTitle } from 'utils/helper';

const PageContext = createContext();

const getUpdatedSubtasks = (templateSubtasks, changesSubtasks) => {
    let updatedSubtasks = templateSubtasks;
    if (changesSubtasks) {
        const toAdd = changesSubtasks.filter((subtask) => subtask.action === 'add');
        const toEdit = changesSubtasks.filter((subtask) => subtask.action === 'edit');
        const toDelete = changesSubtasks.filter((subtask) => subtask.action === 'delete');
        updatedSubtasks = [
            ...templateSubtasks
                .filter((subtask) => {
                    return !toDelete.some((s) => s.id === subtask.id);
                })
                .map((subtask) => {
                    const targetSubtask = toEdit.find((s) => s.id === subtask.id);
                    return targetSubtask ? omit(targetSubtask, ['action']) : subtask;
                }),
            ...toAdd.map((subtask) => omit(subtask, ['action'])),
        ];
    }
    return updatedSubtasks;
};

const options = [
    { key: 'templates', value: 'Task Templates', content: <TaskTemplates /> },
    { key: 'milestones', value: 'Milestones', content: <Milestones /> },
];

const DEFAULT_FORMATIVE_FORM = {
    rubric: [
        {
            id: uuid.v4(),
            title: 'Criterion Name',
            description: 'Description',
            options: [
                {
                    id: uuid.v4(),
                    rate: '',
                    title: '',
                    description: '',
                },
                {
                    id: uuid.v4(),
                    rate: '',
                    title: '',
                    description: '',
                },
            ],
        },
    ],
    rubricSort: 'desc',
    assignmentDescription: '',
    additionalSpecifications: '',
    pastAssignment: '',
    grading: {},
    exampleAssignment: '',
};

const PageProvider = (props) => {
    const { auth } = useAuth();
    const {
        taskTemplates,
        setTaskTemplates,
        createTaskTemplate,
        updateTaskTemplate,
        deleteTaskTemplate,
        publishTaskTemplate,
        unpublishTaskTemplate,
    } = useInstructor();
    const { message } = useSnackbar();
    const { course, formatives } = useUser2();

    // general
    const [tab, setTab] = useState(options[0].key);
    const [confirmationModal, setConfirmationModal] = useState({
        visible: false,
        destructive: true,
        title: '',
        content: '',
        confirmText: '',
        onConfirm: () => {},
        onCancel: () => {},
    });

    // templates
    const [checkedTemplates, setCheckedTemplates] = useState([]);
    const [didFetchCompletion, setDidFetchCompletion] = useState(false);
    const [files, setFiles] = useState({});
    const [sharedUrls, setSharedUrls] = useState({});
    const [changes, setChanges] = useState({});
    const [drawerState, setDrawerState] = useState({
        isOpen: false,
        templateId: '',
    });
    const [filterState, setFilterState] = useState({
        isOpen: false,
        sortKey: 'none',
        statusFilterKeys: [],
        dueDateFilterKeys: [],
        searchInput: '',
    });
    const [uploadResources, setUploadResources] = useState({ isOpen: false });
    const [scheduleModal, setScheduleModal] = useState({ visible: false });

    // milestones
    const spawnedTasks = useRef({});
    const [checkedMilestones, setCheckedMilestones] = useState([]);
    const [view, setView] = useState('milestone');
    const [activeTemplateId, setActiveTemplateId] = useState('');
    const [templateChanges, setTemplateChanges] = useState({});
    const [milestoneDrawer, setMilestoneDrawer] = useState({
        isOpen: false,
        milestoneId: '',
    });
    const [submissions, setSubmissions] = useState(null);

    // formative feedback
    const [formativeDialog, setFormativeDialog] = useState({ open: false });
    const [formativeForm, setFormativeForm] = useState(DEFAULT_FORMATIVE_FORM);
    const [formativeErrors, setFormativeErrors] = useState({});
    const [shouldValidateOnChange, setShouldValidateOnChange] = useState(false);
    const [addEditCriteriaDialog, setAddEditCriteriaDialog] = useState({
        open: false,
        criterionId: '',
        title: '',
        description: '',
    });

    const templateController = {};
    templateController.create = (milestoneId = '') => {
        const id = uuid.v4();
        const now = new Date().getTime();
        const newTaskTemplate = {
            id,
            active: true,
            owner_account_id: auth.id,
            created_on: now,
            modified_on: now,
            end_on: now,
            published_on: 0,
            course_id: auth.course_id,
            title: '',
            workload: 'low',
            description: '',
            visible_to_all: true,
            visible_to: [],
            task_ids: [],
            subtasks: [],
            milestone_id: milestoneId,
            isLocal: true,
        };
        setTaskTemplates((current) => [...current, newTaskTemplate]);
        return newTaskTemplate;
    };
    templateController.duplicate = (originalTemplateId) => {
        const now = new Date().getTime();
        const originalTemplate = taskTemplates.find(({ id }) => id === originalTemplateId);
        const newTemplate = omit(cloneDeep(originalTemplate), ['_id']);

        newTemplate.id = uuid.v4();
        newTemplate.owner_account_id = auth.owner_account_id;
        newTemplate.created_on = now;
        newTemplate.modified_on = now;
        newTemplate.published_on = 0;
        newTemplate.title = getDuplicateTitle(originalTemplate.title);
        newTemplate.task_ids = [];
        newTemplate.subtasks = (newTemplate.subtasks || []).map((subtask) => {
            return {
                ...subtask,
                id: uuid.v4(),
                created_on: now,
                subtask_ids: [],
            };
        });
        setTaskTemplates((current) => [...current, newTemplate]);
        createTaskTemplate(newTemplate);
    };
    templateController.update = (templateId, changes) => {
        setTaskTemplates((current) => {
            return current.map((template) => {
                if (template.id !== templateId) return template;
                if (template.isLocal) {
                    return { ...template, ...changes };
                } else {
                    const { subtasks, ...rest } = changes;
                    const updatedSubtasks = getUpdatedSubtasks(template.subtasks, subtasks);
                    return { ...template, subtasks: updatedSubtasks, ...rest };
                }
            });
        });
    };
    templateController.delete = (templateId, showSuccessMessage = true) => {
        const template = taskTemplates.find((template) => template.id === templateId);
        if (!template.isLocal) {
            deleteTaskTemplate(templateId);
            if (showSuccessMessage) {
                message.success(
                    template.published_on > 0 && template.published_on < new Date().getTime()
                        ? 'Your task was successfully unpublished and deleted.'
                        : 'Your task was successfully deleted.'
                );
            }
        }
        setTaskTemplates((prev) => prev.filter((template) => template.id !== templateId));
    };
    templateController.save = (templateId, changes, shouldNotify) => {
        const template = taskTemplates.find((template) => template.id === templateId);
        if (template.isLocal) {
            const newTemplate = omit(template, ['isLocal']);
            setTaskTemplates((current) => {
                return current.map((template) => {
                    return template.id === templateId ? newTemplate : template;
                });
            });
            createTaskTemplate(newTemplate);
            message.success('Draft saved.');
        } else {
            templateController.update(templateId, changes);
            updateTaskTemplate(templateId, changes, shouldNotify);
            message.success('Your task was saved successfully.');
        }
        return template.isLocal;
    };
    templateController.publish = async (templateId, changes, showSuccessMessage = true) => {
        const template = taskTemplates.find((template) => template.id === templateId);
        if (template.isLocal) {
            let newTemplate = omit(template, ['isLocal']);
            newTemplate = await createTaskTemplate(newTemplate);
            setTaskTemplates((prev) => {
                return prev.map((template) => {
                    return template.id === templateId
                        ? { ...newTemplate, published_on: new Date().getTime() }
                        : template;
                });
            });
            publishTaskTemplate(templateId, {});
        } else {
            templateController.update(templateId, { ...changes, published_on: new Date().getTime() });
            publishTaskTemplate(templateId, changes);
        }
        if (showSuccessMessage) message.success('Your task was successfully published.');
        return template.isLocal;
    };
    templateController.unpublish = (templateId) => {
        templateController.update(templateId, { published_on: 0 });
        unpublishTaskTemplate(templateId);
        message.success('Your task was successfully unpublished.');
    };
    templateController.schedule = async (templateId, changes, timestamp) => {
        const template = taskTemplates.find((template) => template.id === templateId);
        if (template.isLocal) {
            template.published_on = timestamp;
            await createTaskTemplate(omit(template, ['isLocal']));
            setTaskTemplates((current) => {
                return current.map((template) => {
                    return template.id === templateId ? omit(template, ['isLocal']) : template;
                });
            });
        } else {
            templateController.update(templateId, changes);
            updateTaskTemplate(templateId, changes);
        }
        message.success(
            `Your task has successfully been scheduled to be published on ${moment(
                changes.published_on || template.published_on
            ).format('MMMM D[,] YYYY')}.`
        );
        return template.isLocal;
    };
    templateController.unschedule = (templateId) => {
        templateController.update(templateId, { published_on: 0 });
        updateTaskTemplate(templateId, { published_on: 0 });
        message.success('Your task was successfully unscheduled.');
    };

    const openMilestone = (milestoneId) => {
        setTab('milestones');
        setMilestoneDrawer({ isOpen: true, milestoneId });
    };

    useEffect(() => {
        if (!milestoneDrawer.milestoneId) {
            setSubmissions(null);
            return;
        } else {
            (async () => {
                const submissions = await courseService.listCourseSubmissions(
                    course.id,
                    JSON.stringify({ active: true, type: TYPES.MILESTONE, milestone_id: milestoneDrawer.milestoneId })
                );
                setSubmissions(submissions);
            })();
        }
    }, [milestoneDrawer.milestoneId]);

    useEffect(() => {
        if (formativeDialog.open && formatives) {
            const existingFormative = formatives.find(
                ({ milestone_id }) => milestone_id === milestoneDrawer.milestoneId
            );
            if (!existingFormative) {
                setFormativeForm(DEFAULT_FORMATIVE_FORM);
            } else {
                setFormativeForm({
                    rubric: existingFormative.rubric,
                    rubricSort: existingFormative.rubricSort,
                    assignmentDescription: existingFormative.description,
                    additionalSpecifications: existingFormative.custom_context,
                    pastAssignment: existingFormative.baseline[0].sample_submission,
                    grading: existingFormative.baseline[0].grading,
                    exampleAssignment: '',
                });
            }
        }
    }, [formativeDialog.open, formatives, milestoneDrawer.milestoneId]);

    const value = {
        options,
        tab,
        setTab,
        confirmationModal,
        setConfirmationModal,

        // templates
        checkedTemplates,
        setCheckedTemplates,
        didFetchCompletion,
        setDidFetchCompletion,
        files,
        setFiles,
        sharedUrls,
        setSharedUrls,
        changes,
        setChanges,
        drawerState,
        setDrawerState,
        filterState,
        setFilterState,
        uploadResources,
        setUploadResources,
        scheduleModal,
        setScheduleModal,
        openMilestone,

        // milestones
        spawnedTasks: spawnedTasks.current,
        checkedMilestones,
        setCheckedMilestones,
        view,
        setView,
        activeTemplateId,
        setActiveTemplateId,
        templateChanges,
        setTemplateChanges,
        milestoneDrawer,
        setMilestoneDrawer,
        submissions,
        templateController,

        // formative feedback
        formativeDialog,
        setFormativeDialog,
        formativeForm,
        setFormativeForm,
        formativeErrors,
        setFormativeErrors,
        shouldValidateOnChange,
        setShouldValidateOnChange,
        addEditCriteriaDialog,
        setAddEditCriteriaDialog,
    };

    return <PageContext.Provider value={value} {...props} />;
};

export { PageContext };
export default PageProvider;
